import {
  AlertCircle,
  CheckCircle,
  Clock,
  Upload,
  Video,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { bugsnagClient } from "../bugsnag";
import api from "../utils/api";
import fetchWithRetry from "../utils/fetchWithRetry";

const VideoUploadWizard = ({
  onClose,
  projectId,
  onUploadComplete,
  sectionId,
}) => {
  const { t } = useTranslation();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle, uploading, processing, success, error
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadStage, setUploadStage] = useState(""); // preparing, uploading, finalizing
  const fileInputRef = useRef(null);
  const [mpCtx, setMpCtx] = useState(null); // multipart context: { key, uploadId, partSize, partCount, parts, nextPartNumber }

  const handleVideoSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("video/") && file.size <= 20000000000) {
      // 4GB limit
      setSelectedVideo(file);
    } else if (file) {
      alert(t("video_upload.invalid_file"));
    }
  };

  const removeVideo = () => {
    setSelectedVideo(null);
  };

  const uploadVideo = async () => {
    if (!selectedVideo) return;

    setUploading(true);
    setUploadStatus("uploading");
    setUploadProgress(0);
    setUploadStage("preparing");

    try {
      // Simulate preparation phase
      setTimeout(() => setUploadStage("uploading"), 500);

      // Multipart path
      const { key, uploadId } = await api.initiateMultipartVideo(
        projectId,
        sectionId,
        selectedVideo.name,
        selectedVideo.type || "video/mp4",
      );

      const partSize = 10 * 1024 * 1024; // 10MB parts (adjust as needed)
      const partCount = Math.ceil(selectedVideo.size / partSize);
      const parts = [];
      setMpCtx({
        key,
        uploadId,
        partSize,
        partCount,
        parts,
        nextPartNumber: 1,
      });

      for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(partNumber * partSize, selectedVideo.size);
        const blobPart = selectedVideo.slice(start, end);

        const { uploadUrl } = await api.presignMultipartPart(
          key,
          uploadId,
          partNumber,
        );
        const uploadResp = await fetchWithRetry(uploadUrl, {
          method: "PUT",
          body: blobPart,
        });

        if (!uploadResp.ok) throw new Error(`Part ${partNumber} upload failed`);
        const eTag =
          uploadResp.headers.get("ETag") || uploadResp.headers.get("etag");
        // ETag may be missing due to CORS; send null and let server fallback to listParts
        parts.push({
          ETag: eTag ? eTag.replaceAll('"', "") : null,
          PartNumber: partNumber,
        });

        const progress = Math.floor((partNumber / partCount) * 99);
        setUploadProgress(progress);
        setMpCtx({
          key,
          uploadId,
          partSize,
          partCount,
          parts: [...parts],
          nextPartNumber: partNumber + 1,
        });
      }
      setUploadStage("finalizing");

      const result = await api.completeMultipartVideo(
        projectId,
        sectionId,
        key,
        uploadId,
        parts,
      );

      setUploadProgress(100);
      setUploadStage("completed");
      setUploadStatus("processing");
      setUploadResult(result);
      setMpCtx(null);

      // Show processing state briefly
      setTimeout(() => {
        setUploadStatus("success");
      }, 1000);

      // Notify parent component about the upload
      if (onUploadComplete) {
        onUploadComplete(result);
      }

      // Close the modal after showing success
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Upload error:", error);
      toast.error(error.message);
      setUploadStatus("error");
      setUploadStage("error");
      bugsnagClient.notify(error);
    }
  };

  const resumeUpload = async () => {
    if (!mpCtx || !selectedVideo) return;

    setUploadStatus("uploading");
    setUploadStage("uploading");
    try {
      const { key, uploadId, partSize, partCount } = mpCtx;
      const parts = [...(mpCtx.parts || [])];
      for (
        let partNumber = mpCtx.nextPartNumber;
        partNumber <= partCount;
        partNumber += 1
      ) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(partNumber * partSize, selectedVideo.size);
        const blobPart = selectedVideo.slice(start, end);

        const { uploadUrl } = await api.presignMultipartPart(
          key,
          uploadId,
          partNumber,
        );
        const uploadResp = await fetchWithRetry(uploadUrl, {
          method: "PUT",
          body: blobPart,
        });
        if (!uploadResp.ok) {
          throw new Error(`Part ${partNumber} upload failed`);
        }
        const eTag =
          uploadResp.headers.get("ETag") || uploadResp.headers.get("etag");
        parts.push({
          ETag: eTag ? eTag.replaceAll('"', "") : null,
          PartNumber: partNumber,
        });
        const progress = Math.floor((partNumber / partCount) * 99);
        setUploadProgress(progress);
        setMpCtx({
          key,
          uploadId,
          partSize,
          partCount,
          parts: [...parts],
          nextPartNumber: partNumber + 1,
        });
      }
      setUploadStage("finalizing");
      const result = await api.completeMultipartVideo(
        projectId,
        sectionId,
        mpCtx.key,
        mpCtx.uploadId,
        parts,
      );
      setUploadProgress(100);
      setUploadStage("completed");
      setUploadStatus("processing");
      setUploadResult(result);
      setMpCtx(null);
      setTimeout(() => setUploadStatus("success"), 1000);
      if (onUploadComplete) onUploadComplete(result);
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      console.error("Resume upload error:", error);
      toast.error(error.message);
      setUploadStatus("error");
      setUploadStage("error");
      bugsnagClient.notify(error);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{t("video_upload.title")}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {uploading && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-lg">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-blue-100">
              {/* Status Icon */}
              <div className="flex justify-center mb-6">
                {uploadStatus === "uploading" && (
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-tr from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                      <Upload className="w-8 h-8 text-white animate-pulse" />
                    </div>
                    <div className="absolute inset-0 w-20 h-20 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                )}
                {uploadStatus === "processing" && (
                  <div className="w-20 h-20 bg-gradient-to-tr from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                    <Clock className="w-8 h-8 text-white animate-pulse" />
                  </div>
                )}
                {uploadStatus === "success" && (
                  <div className="w-20 h-20 bg-gradient-to-tr from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                )}
                {uploadStatus === "error" && (
                  <div className="w-20 h-20 bg-gradient-to-tr from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                    <AlertCircle className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>

              {/* Title and Stage */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {uploadStatus === "uploading" &&
                    t("video_upload.status_uploading")}
                  {uploadStatus === "processing" &&
                    t("video_upload.status_processing")}
                  {uploadStatus === "success" &&
                    t("video_upload.status_success")}
                  {uploadStatus === "error" && t("video_upload.status_error")}
                </h3>
                <p className="text-sm text-gray-600 capitalize">
                  {uploadStage === "preparing" &&
                    t("video_upload.stage_preparing")}
                  {uploadStage === "uploading" &&
                    t("video_upload.stage_uploading")}
                  {uploadStage === "finalizing" &&
                    t("video_upload.stage_finalizing")}
                  {uploadStage === "completed" &&
                    t("video_upload.stage_completed")}
                  {uploadStage === "error" && t("video_upload.stage_error")}
                  {uploadStatus === "processing" &&
                    t("video_upload.processing_hint")}
                  {uploadStatus === "success" &&
                    t("video_upload.ready_for_doc")}
                </p>
              </div>

              {/* File Info */}
              {selectedVideo && uploadStatus !== "success" && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <Video className="w-6 h-6 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedVideo.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(selectedVideo.size / (1024 * 1024)).toFixed(1)} MB
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              {(uploadStatus === "uploading" ||
                uploadStatus === "processing") && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>{t("video_upload.progress")}</span>
                    <span>
                      {uploadStatus === "uploading"
                        ? `${uploadProgress}%`
                        : t("video_upload.processing")}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ease-out ${
                        uploadStatus === "uploading"
                          ? "bg-gradient-to-r from-blue-500 to-blue-600"
                          : "bg-gradient-to-r from-orange-500 to-orange-600 animate-pulse"
                      }`}
                      style={{
                        width: `${
                          uploadStatus === "uploading" ? uploadProgress : 100
                        }%`,
                      }}
                    >
                      {uploadStatus === "uploading" && (
                        <div className="h-full bg-white bg-opacity-30 rounded-full animate-pulse"></div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Success/Error Message */}
              {uploadStatus === "success" && uploadResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 text-sm font-medium">
                    {t("video_upload.success_message")}
                  </p>
                  {uploadResult.processing?.jobId && (
                    <p className="text-green-700 text-xs mt-1">
                      {t("video_upload.job_id")}:{" "}
                      {uploadResult.processing.jobId}
                    </p>
                  )}
                </div>
              )}

              {uploadStatus === "error" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800 text-sm font-medium">
                    {t("video_upload.failed_try_again")}
                  </p>
                  {mpCtx && (
                    <button
                      onClick={resumeUpload}
                      className="mt-2 mr-3 text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {t("video_upload.resume")}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setUploadStatus("idle");
                      setUploading(false);
                      setUploadProgress(0);
                      setUploadStage("");
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                  >
                    {t("video_upload.try_again")}
                  </button>
                </div>
              )}

              {/* Auto-close indicator */}
              {uploadStatus === "success" && (
                <div className="text-center">
                  <p className="text-xs text-gray-500">
                    {t("video_upload.auto_close")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept="video/*"
              className="hidden"
              id="video-upload"
              ref={fileInputRef}
              onChange={handleVideoSelect}
            />
            <label htmlFor="video-upload" className="cursor-pointer">
              <div className="space-y-2">
                <div className="mx-auto h-12 w-12 text-gray-400">
                  <Video className="h-12 w-12" />
                </div>
                <div className="text-gray-600">
                  <span className="text-blue-600 hover:text-blue-500">
                    {t("video_upload.cta")}
                  </span>{" "}
                  {t("video_upload.or_drag_and_drop")}
                </div>
                <p className="text-xs text-gray-500">
                  {t("video_upload.file_hint")}
                </p>
              </div>
            </label>
          </div>

          {selectedVideo && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <Video className="h-8 w-8 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedVideo.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedVideo.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={removeVideo}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              {t("common.cancel")}
            </button>
            <button
              onClick={uploadVideo}
              disabled={!selectedVideo || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
            >
              {uploading
                ? t("video_upload.uploading")
                : t("video_upload.upload")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploadWizard;
